import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useAuth } from '../../auth/useAuth';
import { APP_VERSION } from '../../constants/changelog';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateX(-30px) scale(0.96); }
  to { opacity: 1; transform: translateX(0) scale(1); }
`;

const badgeGlow = keyframes`
  0%, 100% { filter: drop-shadow(0 0 12px rgba(180,160,100,0.3)); }
  50% { filter: drop-shadow(0 0 24px rgba(180,160,100,0.5)); }
`;

export const LoginPage = () => {
  const { isAuthenticated, isLoading, login } = useAuth();
  const { inProgress } = useMsal();
  const navigate = useNavigate();
  const [redirectHandled, setRedirectHandled] = useState(false);

  useEffect(() => {
    if (inProgress === InteractionStatus.None) setRedirectHandled(true);
  }, [inProgress]);

  useEffect(() => {
    if (redirectHandled && isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, redirectHandled, navigate]);

  const showLoading = !redirectHandled || isLoading || inProgress !== InteractionStatus.None;

  return (
    <Box sx={{
      height: '100vh', display: 'flex', overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Full-screen background */}
      <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        <img src="/background-v2.png" alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'right center' }} />
      </Box>

      {/* Left panel — no overlay, just holds the card */}
      <Box sx={{
        position: 'relative', zIndex: 10,
        width: { xs: '100%', md: '45%', lg: '38%', xl: '34%' },
        minWidth: { md: 440 },
        height: '100%',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        px: { xs: 3, md: 5, lg: 6 },
      }}>
        {/* Card — unified glassmorphism card */}
        <Box sx={{
          width: '100%', maxWidth: 380,
          borderRadius: '24px', overflow: 'hidden', position: 'relative',
          animation: `${fadeIn} 0.8s cubic-bezier(0.16, 1, 0.3, 1)`,
          border: '1px solid rgba(255,255,255,0.15)',
          boxShadow: '0 30px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)',
          background: 'linear-gradient(170deg, rgba(20,30,20,0.25) 0%, rgba(30,42,30,0.18) 40%, rgba(255,255,255,0.05) 100%)',
          backdropFilter: 'blur(28px) saturate(1.4)',
          WebkitBackdropFilter: 'blur(28px) saturate(1.4)',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          pt: 4, pb: 5, px: 3.5,
        }}>
          {/* App badge — at the top */}
          <Box component="img" src="/moftaki-circle.webp" alt="ALIS"
            sx={{
              width: { xs: 375, md: 425, xl: 475 }, maxWidth: '95%',
              animation: `${badgeGlow} 4s ease-in-out infinite`,
            }}
          />

          {/* Sign in area */}
          <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            width: '100%', mt: 2,
          }}>
            <Typography sx={{
              color: alpha('#f3ecd2', 0.95), mb: 2.5,
              fontSize: '0.98rem', letterSpacing: '0.03em', fontWeight: 500,
              fontFamily: "'Georgia', serif", fontStyle: 'italic',
              textAlign: 'center', whiteSpace: 'nowrap',
              backgroundColor: alpha('#141e14', 0.55),
              border: `1px solid ${alpha('#d4c9a0', 0.2)}`,
              borderRadius: '8px', px: 1.75, py: 0.6,
            }}>
              Advanced Laboratory Information System
            </Typography>

            {showLoading ? (
              <Box sx={{ py: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={22} sx={{ color: '#b4a064' }} />
                <Typography sx={{ fontSize: '1.0rem', color: alpha('#fff', 0.5) }}>
                  Signing you in...
                </Typography>
              </Box>
            ) : (
              <Button variant="contained" fullWidth size="large" onClick={() => login()}
                sx={{
                  textTransform: 'none', fontWeight: 600,
                  py: 1.3, fontSize: '1.3rem', borderRadius: '5px',
                  maxWidth: 300,
                  backgroundColor: '#0a1e5c', color: '#fff',
                  border: '1px solid rgba(100,140,255,0.25)',
                  boxShadow: '0 0 15px rgba(30,60,160,0.5), 0 4px 12px rgba(0,0,0,0.3)',
                  transition: 'all 0.2s ease', gap: 1.5,
                  '&:hover': {
                    backgroundColor: '#0d2670',
                    boxShadow: '0 0 20px rgba(30,60,160,0.6), 0 6px 16px rgba(0,0,0,0.4)',
                  },
                  '&:active': { transform: 'scale(0.98)' },
                }}>
                <Box component="img" src="/ms-logo.svg" alt="" sx={{ width: 18, height: 18, flexShrink: 0 }} />
                Sign in with BGC
              </Button>
            )}

            <Typography sx={{
              mt: 1.5, color: alpha('#d4c9a0', 0.3),
              fontSize: '0.85rem', letterSpacing: '0.02em',
            }}>
              Sign in with your BGC SSO account
            </Typography>
          </Box>

          {/* Bottom — BGC logo inline with text */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 4 }}>
            <Box component="img" src="/BGC-logo.png" alt="BGC Engineering"
              sx={{ width: 64, flexShrink: 0 }} />
            <Box>
              <Typography sx={{
                color: alpha('#fff', 0.35), fontWeight: 600,
                fontSize: '0.75rem',
                letterSpacing: '0.22em', textTransform: 'uppercase',
                textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                lineHeight: 1.3,
              }}>
                BGC Engineering Inc.
              </Typography>
              <Typography sx={{
                color: alpha('#fff', 0.18),
                fontSize: '0.69rem', letterSpacing: '0.06em',
              }}>
                {APP_VERSION}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
